import React, { useEffect, useState } from 'react';
import { getAuthToken } from '../utils/api';
import { resolveApiUrl } from '../utils/apiBase';

/**
 * An <img> for pictures served by the API.
 *
 * A plain <img src="/api/images/x.png"> is fetched by the browser itself, and
 * the browser attaches no bearer token - only fetch() calls go through the
 * wrapper that adds one. Once App Service Authentication was turned on with
 * requireAuthentication, every such request was answered 401 by the platform
 * before the function ran, and the user saw a broken image or, on the admin
 * preview, the platform's own "You do not have permission to view this
 * directory or page." page.
 *
 * So the bytes are fetched the same way as every other API call and handed to
 * the <img> as an object URL. The alternative - excluding /api/images from
 * authentication - would publish every image in the wiki to anyone holding a
 * URL, which is the opposite of what GetImage's AnyAccess policy asks for.
 */
interface AuthenticatedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    src?: string;
    /** Rendered instead of the image if it cannot be loaded. */
    fallback?: React.ReactNode;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ src, alt, fallback, ...imgProps }) => {
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!src) {
            setObjectUrl(null);
            setFailed(true);
            return;
        }

        // Anything that is not an API path - a data URI, a bundled asset, an
        // absolute URL elsewhere - needs no token and no fetch.
        const resolved = resolveApiUrl(src);
        if (!/\/api\/images\//.test(resolved)) {
            setObjectUrl(resolved);
            setFailed(false);
            return;
        }

        let cancelled = false;
        let created: string | null = null;

        (async () => {
            setFailed(false);
            try {
                const token = await getAuthToken();
                const response = await fetch(resolved, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!response.ok) throw new Error(`${response.status}`);
                const blob = await response.blob();
                if (cancelled) return;
                created = URL.createObjectURL(blob);
                setObjectUrl(created);
            } catch {
                if (!cancelled) {
                    setObjectUrl(null);
                    setFailed(true);
                }
            }
        })();

        return () => {
            cancelled = true;
            // Revoking on unmount matters here: an article can carry dozens of
            // images, and an object URL is held until it is released.
            if (created) URL.revokeObjectURL(created);
        };
    }, [src]);

    if (failed) return <>{fallback ?? null}</>;
    if (!objectUrl) return null;

    return <img src={objectUrl} alt={alt} {...imgProps} />;
};

export default AuthenticatedImage;
